import { createFileRoute, Link } from "@tanstack/react-router";
import { RouterOutputs, trpc } from "@/hooks/trpc";
import { useCurrentOperator } from "@/hooks/use-operator";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useZodForm, Form, FormSelect } from "@/components/form";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BatchClearanceStatusEnum,
  batchStatusEnum,
  brewBatchMutator,
  brewBatchId,
  batchClearanceStatusEnum,
  type BrewBatchId,
  AttributeKindEnum,
  CpLevelEnum,
} from "@brewbase/db/types";
import {
  SimpleSelect,
  SimpleSelectOption,
} from "@/components/ui/simple-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatDateTime } from "@/lib/date";
import {
  EditIcon,
  ExternalLinkIcon,
  SaveIcon,
  CheckIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { getDisplaySymbol, getSkuForItem } from "@brewbase/lib";
import { capitalize, toTitleCase } from "typed-case";
import { CpLevelIcon } from "@/components/ui/cp-level-icon";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { computeSpecStatus, type SpecStatusResult } from "@/lib/spec-status";
import { BatchTestGrid } from "@/features/batches/batch-test-grid";

type ClearanceTestAttribute =
  RouterOutputs["batches"]["brewBatches"]["getEnhancedClearanceData"]["clearanceTests"][number]["attributes"][number];

export const Route = createFileRoute("/harvests/batches/$batch_id")({
  component: RouteComponent,
  params: z.object({
    batch_id: z.coerce.number().transform((val) => brewBatchId.parse(val)),
  }),
});

// Accept date inputs from form as either Date objects or empty strings, converting to Date/null
const toDateOrNull = (val: unknown) => {
  if (val instanceof Date) return val;
  if (typeof val === "string") {
    if (val.trim() === "") return null;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  return val ?? null;
};

const editBatchSchema = brewBatchMutator
  .pick({
    status: true,
    clearance_status: true,
    clearance_decision_reason: true,
    clearance_notes: true,
    actual_weight_kg: true,
    actual_volume_l: true,
    notes: true,
    batch_number: true,
    name: true,
    description: true,
    produced_at: true,
    stored_at: true,
    expiry_date: true,
    released_at: true,
    disposed_at: true,
    // Cannot edit: harvest_id, source_harvest_operation_id, batch_type, responsible_operator_id etc.
  })
  .extend({
    responsible_operator_id: z.number().optional().nullable(),
    // expiry_date is a pg `date`, so brewBatchMutator already types it as a
    // CalendarDate and <Input type="date"> hands back exactly that shape — no
    // preprocess needed. The rest are timestamps and still need one.
    produced_at: z.preprocess(toDateOrNull, z.date().optional().nullable()),
    stored_at: z.preprocess(toDateOrNull, z.date().optional().nullable()),
    released_at: z.preprocess(toDateOrNull, z.date().optional().nullable()),
    disposed_at: z.preprocess(toDateOrNull, z.date().optional().nullable()),
  });
type EditBatchFormValues = z.infer<typeof editBatchSchema>;

const clearanceDecisionSchema = z.object({
  clearance_decision_reason: z.string().optional(),
  clearance_notes: z.string().optional(),
  clearance_status: batchClearanceStatusEnum,
});
type ClearanceDecisionFormValues = z.infer<typeof clearanceDecisionSchema>;

function RouteComponent() {
  const { batch_id } = Route.useParams();
  const utils = trpc.useUtils();
  const [showClearanceForm, setShowClearanceForm] = useState(false);
  const { data: currentOperator } = useCurrentOperator();
  const { data: operators } = trpc.operator.list.useQuery();

  const {
    data: batch,
    isLoading: isLoadingBatch,
    error: batchError,
  } = trpc.batches.brewBatches.getById.useQuery(batch_id);

  const {
    data: enhancedClearanceData,
    isLoading: isLoadingEnhancedClearance,
    error: enhancedClearanceError,
  } = trpc.batches.brewBatches.getEnhancedClearanceData.useQuery(batch_id, {
    enabled: !!batch,
  });

  const updateBatchMutation = trpc.batches.brewBatches.update.useMutation({
    onSuccess: () => {
      toast.success("Batch details updated!");
      utils.batches.brewBatches.getById.invalidate(batch_id);
    },
    onError: (error) => {
      toast.error(`Failed to update batch: ${error.message}`);
    },
  });

  const updateClearanceMutation = trpc.batches.brewBatches.update.useMutation({
    onSuccess: () => {
      toast.success("Clearance decision updated!");
      utils.batches.brewBatches.getById.invalidate(batch_id);
      setShowClearanceForm(false);
    },
    onError: (error) => {
      toast.error(`Failed to update clearance: ${error.message}`);
    },
  });

  const form = useZodForm({
    schema: editBatchSchema,
    defaultValues: {},
  });

  const clearanceForm = useZodForm({
    schema: clearanceDecisionSchema,
    defaultValues: {
      clearance_status: BatchClearanceStatusEnum.PENDING_TESTS,
    },
  });

  const handleEditSubmit = (values: EditBatchFormValues) => {
    const updatePayload: any = { ...values };

    // Server expects strings (or null) for weight/volume. Normalize accordingly.
    if (values.actual_weight_kg === "" || values.actual_weight_kg === null) {
      updatePayload.actual_weight_kg = null;
    } else if (typeof values.actual_weight_kg !== "string") {
      updatePayload.actual_weight_kg = String(values.actual_weight_kg);
    }

    if (values.actual_volume_l === "" || values.actual_volume_l === null) {
      updatePayload.actual_volume_l = null;
    } else if (typeof values.actual_volume_l !== "string") {
      updatePayload.actual_volume_l = String(values.actual_volume_l);
    }

    // Dates are already preprocessed by zod resolver to Date|null

    updateBatchMutation.mutate({ id: batch_id, ...updatePayload });
  };

  const handleClearanceSubmit = (values: ClearanceDecisionFormValues) => {
    updateClearanceMutation.mutate({
      id: batch_id,
      ...values,
    });
  };

  const batchStatusOptions: SimpleSelectOption[] = Object.values(
    batchStatusEnum.enum,
  ).map((s) => ({ label: toTitleCase(s.toLowerCase()), value: s }));
  const clearanceStatusOptions: SimpleSelectOption[] = Object.values(
    batchClearanceStatusEnum.enum,
  ).map((s) => ({ label: toTitleCase(s.toLowerCase()), value: s }));

  // Set default form values once batch data is available
  useEffect(() => {
    if (batch && form.reset) {
      const defaultVals: Partial<EditBatchFormValues> = {};
      for (const key of Object.keys(editBatchSchema.shape)) {
        if (batch.hasOwnProperty(key)) {
          const batchKey = key as keyof typeof batch;
          const formKey = key as keyof EditBatchFormValues;
          if (batch[batchKey] !== undefined) {
            if (
              [
                "produced_at",
                "stored_at",
                "expiry_date",
                "released_at",
                "disposed_at",
              ].includes(key) &&
              batch[batchKey]
            ) {
              defaultVals[formKey] = formatDate(
                batch[batchKey] as string | Date,
              ) as any;
            } else {
              defaultVals[formKey] = batch[batchKey] as any;
            }
          }
        }
      }
      (defaultVals as any).responsible_operator_id =
        (batch as any).responsible_operator_id ?? null;
      form.reset(defaultVals);
    }
  }, [batch, form.reset]);

  // Set clearance form defaults
  useEffect(() => {
    if (batch && clearanceForm.reset) {
      clearanceForm.reset({
        clearance_status: batch.clearance_status,
        clearance_decision_reason: batch.clearance_decision_reason || "",
        clearance_notes: batch.clearance_notes || "",
      });
    }
  }, [batch, clearanceForm.reset]);

  const canMakeClearanceDecision =
    batch?.status !== "PLANNED" &&
    !batch?.clearance_decided_at &&
    (batch?.clearance_status === "PENDING_APPROVAL" ||
      batch?.clearance_status === "TESTS_IN_PROGRESS" ||
      batch?.clearance_status === "PENDING_TESTS");

  if (isLoadingBatch) return <p>Loading batch details...</p>;
  if (batchError)
    return (
      <p className="text-destructive">
        Error loading batch: {batchError.message}
      </p>
    );
  if (!batch) return <p>Batch not found.</p>;

  return (
    <div className="container mx-auto p-4 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-2xl">{batch.name}</CardTitle>
            <CardDescription>
              {capitalize(batch.batch_type.toLowerCase())} Batch
            </CardDescription>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <EditIcon className="size-4 mr-2" />
                Edit Batch
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Batch: {batch.name}</DialogTitle>
              </DialogHeader>
              <Form
                form={form}
                onSubmit={handleEditSubmit}
                renderSubmitButton={false}
                className="space-y-4 max-h-[70vh] overflow-y-auto p-1 pr-3"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input {...form.register("name")} />
                  </div>
                  <div>
                    <Label>External Batch #</Label>
                    <Input {...form.register("batch_number")} />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea {...form.register("description")} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <FormSelect name="status" options={batchStatusOptions} />
                  </div>
                  <div>
                    <Label>Responsible Operator</Label>
                    <FormSelect
                      name="responsible_operator_id"
                      options={
                        operators?.map((op) => ({
                          label:
                            op.full_name ||
                            `${op.first_name ?? ""} ${op.last_name ?? ""}`.trim() ||
                            `Operator ${op.id}`,
                          value: op.id,
                        })) ?? []
                      }
                    />
                  </div>
                  <div>
                    <Label>Actual Weight (kg)</Label>
                    <NumericInput
                      precise
                      value={form.watch("actual_weight_kg") ?? null}
                      onValueChange={(v) =>
                        form.setValue("actual_weight_kg", v ?? "")
                      }
                    />
                  </div>
                  <div>
                    <Label>Actual Volume (L)</Label>
                    <NumericInput
                      precise
                      value={form.watch("actual_volume_l") ?? null}
                      onValueChange={(v) =>
                        form.setValue("actual_volume_l", v ?? "")
                      }
                    />
                  </div>
                </div>

                <h3 className="text-lg font-semibold pt-2 border-b">
                  Clearance
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Clearance Status</Label>
                    <FormSelect
                      name="clearance_status"
                      options={clearanceStatusOptions}
                    />
                  </div>
                </div>
                <div>
                  <Label>Clearance Decision Reason</Label>
                  <Textarea {...form.register("clearance_decision_reason")} />
                </div>
                <div>
                  <Label>Clearance Notes</Label>
                  <Textarea {...form.register("clearance_notes")} />
                </div>

                <h3 className="text-lg font-semibold pt-2 border-b">
                  Lifecycle Dates
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Produced At</Label>
                    <Input type="date" {...form.register("produced_at")} />
                  </div>
                  <div>
                    <Label>Stored At</Label>
                    <Input type="date" {...form.register("stored_at")} />
                  </div>
                  <div>
                    <Label>Expiry Date</Label>
                    <Input type="date" {...form.register("expiry_date")} />
                  </div>
                  <div>
                    <Label>Released At</Label>
                    <Input type="date" {...form.register("released_at")} />
                  </div>
                  <div>
                    <Label>Disposed At</Label>
                    <Input type="date" {...form.register("disposed_at")} />
                  </div>
                </div>

                <div>
                  <Label>General Notes</Label>
                  <Textarea {...form.register("notes")} />
                </div>

                <DialogFooter className="pt-4">
                  <DialogClose asChild>
                    <Button type="button" variant="outline">
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button type="submit" className="flex items-center gap-2">
                    <SaveIcon className="size-4" />
                    Save Changes
                  </Button>
                </DialogFooter>
              </Form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
            <div>
              <dt className="font-medium">Status</dt>
              <dd>
                <Badge
                  variant={
                    batch.status === "CLEARED_FOR_RELEASE"
                      ? "default"
                      : "outline"
                  }
                >
                  {batch.status}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="font-medium">Weight</dt>
              <dd className="text-muted-foreground">
                {batch.actual_weight_kg ?? "-"} kg
              </dd>
            </div>

            {batch.actual_volume_l && (
              <div>
                <dt className="font-medium">Volume</dt>
                <dd className="text-muted-foreground">
                  {batch.actual_volume_l} L
                </dd>
              </div>
            )}

            <div>
              <dt className="font-medium">Responsible Operator</dt>
              <dd className="text-muted-foreground">
                {batch.responsible_operator_name ?? "-"}
              </dd>
            </div>

            <div>
              <dt className="font-medium">Produced By</dt>
              <dd className="text-muted-foreground">
                {batch.produced_by_name ?? "-"}
              </dd>
            </div>

            <div>
              <dt className="font-medium">Storage Location</dt>
              <dd className="text-muted-foreground">
                {batch.storage_location_name ?? "-"}
              </dd>
            </div>

            <div>
              <dt className="font-medium">Produced At</dt>
              <dd className="text-muted-foreground">
                {batch.produced_at ? formatDateTime(batch.produced_at) : "-"}
              </dd>
            </div>

            <div>
              <dt className="font-medium">Stored At</dt>
              <dd className="text-muted-foreground">
                {batch.stored_at ? formatDateTime(batch.stored_at) : "-"}
              </dd>
            </div>

            <div>
              <dt className="font-medium">Expiry Date</dt>
              <dd className="text-muted-foreground">
                {batch.expiry_date ? formatDate(batch.expiry_date) : "-"}
              </dd>
            </div>

            {batch.released_at && (
              <div>
                <dt className="font-medium">Released At</dt>
                <dd className="text-muted-foreground">
                  {formatDateTime(batch.released_at)}
                </dd>
              </div>
            )}
            {batch.disposed_at && (
              <div>
                <dt className="font-medium">Disposed At</dt>
                <dd className="text-muted-foreground">
                  {formatDateTime(batch.disposed_at)}
                </dd>
              </div>
            )}
          </dl>
          {batch.description && (
            <div>
              <h4 className="font-medium">Description</h4>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {batch.description}
              </p>
            </div>
          )}
          {batch.notes && (
            <div>
              <h4 className="font-medium">Notes</h4>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {batch.notes}
              </p>
            </div>
          )}

          {batch.harvest_id && batch.harvest_group_id && (
            <Link
              to="/harvests/$session_id"
              params={{ session_id: batch.harvest_group_id }}
              className="text-muted-foreground hover:underline flex items-center gap-1"
            >
              View Harvest Journal <ExternalLinkIcon className="size-4" />
            </Link>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Clearance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-10 gap-y-1 max-w-max">
              <div>
                <dt className="">Clearance Procedure</dt>
                <dd className="text-muted-foreground">
                  {batch.clearance_definition_name || "N/A"}
                </dd>
              </div>
              <div>
                <dt className="">Clearance Status</dt>
                <dd>
                  <Badge
                    variant={
                      batch.clearance_status === "CLEARED"
                        ? "default"
                        : batch.clearance_status === "REJECTED"
                          ? "destructive"
                          : "outline"
                    }
                  >
                    {toTitleCase(batch.clearance_status.toLowerCase())}
                  </Badge>
                </dd>
              </div>
              {batch.clearance_decided_at && (
                <div>
                  <dt className="">Decision At</dt>
                  <dd className="text-muted-foreground">
                    {formatDateTime(batch.clearance_decided_at)}
                  </dd>
                </div>
              )}
              {batch.clearance_decided_by_operator_id && (
                <div>
                  <dt className="">Decided By</dt>
                  <dd className="text-muted-foreground">
                    Operator {batch.clearance_decided_by_operator_id}
                  </dd>
                </div>
              )}{" "}
              {/* TODO: Fetch operator name */}
            </dl>
            {batch.clearance_decision_reason && (
              <div>
                <h4 className="">Decision Reason</h4>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {batch.clearance_decision_reason}
                </p>
              </div>
            )}
            {batch.clearance_notes && (
              <div>
                <h4 className="">Clearance Notes</h4>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {batch.clearance_notes}
                </p>
              </div>
            )}
          </div>

          {canMakeClearanceDecision && (
            <div className="mb-10">
              <div className="mb-2">
                <h4 className="font-medium">Make Clearance Decision</h4>
                {!showClearanceForm && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        updateClearanceMutation.mutate({
                          id: batch_id,
                          clearance_status: BatchClearanceStatusEnum.CLEARED,
                          clearance_decision_reason:
                            "Approved - all criteria met",
                        });
                      }}
                      disabled={updateClearanceMutation.isPending}
                      className="bg-[var(--success)] hover:bg-[var(--success)]/90 text-[var(--success-foreground)]"
                    >
                      <CheckIcon className="size-4 mr-2" />
                      {updateClearanceMutation.isPending
                        ? "Approving..."
                        : "Quick Approve"}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        updateClearanceMutation.mutate({
                          id: batch_id,
                          clearance_status: BatchClearanceStatusEnum.REJECTED,
                          clearance_decision_reason:
                            "Rejected - criteria not met",
                        });
                      }}
                      disabled={updateClearanceMutation.isPending}
                    >
                      <XIcon className="size-4 mr-2" />
                      {updateClearanceMutation.isPending
                        ? "Rejecting..."
                        : "Quick Reject"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowClearanceForm(true)}
                    >
                      <EditIcon className="size-4 mr-2" />
                      Detailed Decision
                    </Button>
                  </div>
                )}
              </div>

              {showClearanceForm && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>
                      Use the form below for detailed clearance decisions with
                      custom reasons and notes.
                    </span>
                  </div>
                  <Form
                    form={clearanceForm}
                    onSubmit={handleClearanceSubmit}
                    className="space-y-4 p-4 border rounded-lg bg-muted/50"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Clearance Status</Label>
                        <FormSelect
                          name="clearance_status"
                          options={clearanceStatusOptions}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Decision Reason</Label>
                      <Textarea
                        {...clearanceForm.register("clearance_decision_reason")}
                        placeholder="Explain the reason for this clearance decision..."
                      />
                    </div>
                    <div>
                      <Label>Clearance Notes</Label>
                      <Textarea
                        {...clearanceForm.register("clearance_notes")}
                        placeholder="Additional notes about the clearance process..."
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="submit"
                        size="sm"
                        disabled={updateClearanceMutation.isPending}
                        className="flex items-center gap-2"
                      >
                        <CheckIcon className="size-4" />
                        {updateClearanceMutation.isPending
                          ? "Updating..."
                          : "Save Decision"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowClearanceForm(false)}
                      >
                        <XIcon className="size-4" />
                        Cancel
                      </Button>
                    </div>
                  </Form>
                </div>
              )}
            </div>
          )}

          <div>
            {isLoadingEnhancedClearance && <p>Loading clearance data...</p>}
            {enhancedClearanceError && (
              <p className="text-destructive">
                Error loading clearance data: {enhancedClearanceError.message}
              </p>
            )}

            {enhancedClearanceData && (
              <ClearanceTable
                data={enhancedClearanceData}
                batchId={batch_id}
                onUpdate={() => {
                  utils.batches.brewBatches.getById.invalidate(batch_id);
                  utils.batches.brewBatches.getEnhancedClearanceData.invalidate(
                    batch_id,
                  );
                }}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <BatchTestGrid batchId={batch_id} batchName={batch.name} />
    </div>
  );
}

interface CcpData {
  attributeName: string;
  attributeKind: AttributeKindEnum;
  opName: string;
  actualValueDisplay: string;
  targetValueDisplay: string;
  status: "in-spec" | "out-of-spec" | "not-recorded" | "not-applicable";
  defaultUnit?: string | null;
  cp_level: CpLevelEnum;
}

function getCcpStatusColor(
  status: CcpData["status"],
  isText: boolean = false,
): string {
  switch (status) {
    case "in-spec":
      return isText
        ? "text-[var(--success-foreground)]"
        : "bg-[var(--success)]/10";
    case "out-of-spec":
      return isText ? "text-[var(--error-foreground)]" : "bg-[var(--error)]/10";
    case "not-recorded":
      return isText ? "text-[var(--muted-foreground)]" : "bg-[var(--muted)]";
    case "not-applicable":
    default:
      return isText ? "text-[var(--muted-foreground)]" : "bg-[var(--accent)]";
  }
}

function getSpecStatusColor(
  status: SpecStatusResult["status"],
  isText: boolean = false,
): string {
  switch (status) {
    case "in-spec":
      return isText
        ? "text-[var(--success-foreground)]"
        : "bg-[var(--success)]/10";
    case "out-of-spec":
      return isText ? "text-[var(--error-foreground)]" : "bg-[var(--error)]/10";
    case "not-recorded":
      return isText ? "text-[var(--muted-foreground)]" : "bg-[var(--muted)]";
    case "not-applicable":
    default:
      return isText ? "text-[var(--muted-foreground)]" : "bg-[var(--accent)]";
  }
}

const formatNumber = (value: number) => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
};

interface ClearanceTableProps {
  data: RouterOutputs["batches"]["brewBatches"]["getEnhancedClearanceData"];
  batchId: BrewBatchId;
  onUpdate: () => void;
}

function ClearanceTable({ data, batchId, onUpdate }: ClearanceTableProps) {
  const { data: currentOperator } = useCurrentOperator();
  const [isOpen, setIsOpen] = useState(() => {
    const isMine =
      !!currentOperator &&
      (data.batch?.clearance_responsible_operator_id === currentOperator.id ||
        data.batch?.responsible_operator_id === currentOperator.id);
    return isMine; // open if responsible, otherwise collapsed by default
  });
  const utils = trpc.useUtils();

  const updateBatchMutation = trpc.batches.brewBatches.update.useMutation({
    onSuccess: () => {
      toast.success("Clearance checks updated!");
      onUpdate();
    },
    onError: (error) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  const handleCheckboxChange = (field: string, checked: boolean) => {
    updateBatchMutation.mutate({
      id: batchId,
      [field]: checked,
    });
  };

  // Calculate overall clearance status
  const isAllClearanceComplete = useMemo(() => {
    const allTestsPass =
      data.clearanceTests?.every(
        (test: any) =>
          test.status === "COMPLETED" &&
          test.attributes.every((attr: any) => {
            if (!attr.is_specification) return true;
            const kind = attr.testTemplateAttribute?.kind;
            if (!kind) return true;
            const status = computeSpecStatus({ ...attr, kind }).status;
            return status === "in-spec" || status === "not-applicable";
          }),
      ) ?? true;

    const notContaminated = data.batch?.outcome !== 'CONTAMINATED';
    const allIngredientsFoodGrade =
      data.inventoryItems?.every((item: any) => item.food_grade) ?? true;
    const methanolSafe =
      !data.lastSample?.methanol_v_v_pct ||
      parseFloat(data.lastSample.methanol_v_v_pct) < 0.1; // Assuming 0.1% is safe threshold

    const allCpsInSpec =
      data.harvestControlPoints?.every(
        (cp: any) => cp.status === "in-spec" || cp.status === "not-applicable",
      ) ?? true;

    const packagingOk = data.batch?.safe_packaging_practice_checked;
    const foreignObjectOk = data.batch?.foreign_object_check_passed;

    return (
      allTestsPass &&
      notContaminated &&
      allIngredientsFoodGrade &&
      methanolSafe &&
      allCpsInSpec &&
      packagingOk &&
      foreignObjectOk
    );
  }, [data]);

  // Automatically collapse if decision has been made
  const isDecisionMade =
    data.batch?.clearance_status === "CLEARED" ||
    data.batch?.clearance_status === "REJECTED";

  useEffect(() => {
    if (isDecisionMade) {
      setIsOpen(false);
    }
  }, [isDecisionMade]);

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg">Clearance Review</CardTitle>
                <Badge
                  variant={isAllClearanceComplete ? "default" : "destructive"}
                >
                  {isAllClearanceComplete
                    ? "Ready for Clearance"
                    : "Issues Detected"}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                {isDecisionMade && (
                  <Badge variant="outline" className="mr-2">
                    Decision Made
                  </Badge>
                )}
                {isOpen ? (
                  <ChevronDownIcon className="size-4" />
                ) : (
                  <ChevronRightIcon className="size-4" />
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Fermentation Status */}
            {data.batch?.fermentation_id && (
              <div>
                <h5 className="font-medium mb-3 flex items-center gap-2">
                  Fermentation {data.batch.fermentation_name}
                  {data.batch.fermentation_id && (
                    <Link
                      to="/fermentations/$group_id"
                      params={{ group_id: data.batch.fermentation_group_id! }}
                      className="ml-1 text-muted-foreground hover:text-primary"
                      title="View Fermentation"
                    >
                      <ExternalLinkIcon className="size-4" />
                    </Link>
                  )}
                </h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        "size-3 rounded-full",
                        data.batch.outcome === 'CONTAMINATED' ? "bg-red-500" : "bg-green-500",
                      )}
                    />
                    <span className="text-sm">
                      {data.batch.outcome === 'CONTAMINATED'
                        ? "Contaminated"
                        : "Not Contaminated"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        "size-3 rounded-full",
                        data.lastSample?.methanol_v_v_pct &&
                          parseFloat(data.lastSample.methanol_v_v_pct) >= 0.1
                          ? "bg-red-500"
                          : "bg-green-500",
                      )}
                    />
                    <span className="text-sm">
                      Methanol:{" "}
                      {data.lastSample?.methanol_v_v_pct
                        ? `${data.lastSample.methanol_v_v_pct}%`
                        : data.batch?.fermentation_id
                          ? "No sample data"
                          : "No fermentation data"}
                      {data.lastSample?.date_time && (
                        <span className="text-muted-foreground ml-1">
                          ({formatDate(data.lastSample.date_time)})
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Ingredient Food Safety */}
            {data.inventoryItems?.length > 0 && (
              <div>
                <h5 className="font-medium mb-3">Ingredient Food Safety</h5>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ingredient</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Food Grade</TableHead>
                      <TableHead>Expiry Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.inventoryItems.map((item: any) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {getSkuForItem({
                            inventory_resource_id: item.resource_id,
                            id: item.id,
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              item.food_grade ? "default" : "destructive"
                            }
                          >
                            {item.food_grade ? "Food Grade" : "Not Food Grade"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.expiry_date
                            ? formatDate(item.expiry_date)
                            : "N/A"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Critical Control Points */}
            {data.harvestControlPoints?.length > 0 && (
              <div>
                <h5 className="font-medium mb-3">Critical Control Points</h5>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Level</TableHead>
                      <TableHead>Control Point</TableHead>
                      <TableHead>Operation</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Actual</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.harvestControlPoints.map((cp: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <CpLevelIcon
                            level={cp.cp_level}
                            showLabel
                            status={cp.status}
                          />
                        </TableCell>
                        <TableCell>
                          {cp.attributeName}
                          {cp.defaultUnit && (
                            <span className="text-xs text-muted-foreground ml-1">
                              ({getDisplaySymbol(cp.defaultUnit)})
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{cp.opName}</TableCell>
                        <TableCell>{cp.targetValueDisplay}</TableCell>
                        <TableCell
                          className={cn(
                            "font-semibold",
                            getCcpStatusColor(cp.status, false),
                          )}
                        >
                          {cp.actualValueDisplay}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={cn(getCcpStatusColor(cp.status, false))}
                            variant={
                              cp.status === "in-spec" ? "default" : "outline"
                            }
                          >
                            {cp.status.replace(/-/g, " ")}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Clearance Tests */}
            <div>
              <h5 className="font-medium mb-3 flex items-center gap-2">
                Clearance Tests
              </h5>
              {data.clearanceTests?.length === 0 ? (
                <p className="text-muted-foreground">
                  No clearance tests configured for this batch.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Test Name</TableHead>
                      <TableHead>Mandatory</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead>Completed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.clearanceTests?.map((test: any) => (
                      <Collapsible key={test.id} asChild>
                        <>
                          <CollapsibleTrigger asChild>
                            <TableRow className="cursor-pointer hover:bg-accent/50">
                              <TableCell>{test.name}</TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    test.was_mandatory
                                      ? "destructive"
                                      : "outline"
                                  }
                                >
                                  {test.was_mandatory
                                    ? "Mandatory"
                                    : "Optional"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    test.status === "COMPLETED"
                                      ? "default"
                                      : "outline"
                                  }
                                >
                                  {test.status}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    test.overall_result === "PASS"
                                      ? "default"
                                      : test.overall_result === "FAIL"
                                        ? "destructive"
                                        : "outline"
                                  }
                                >
                                  {test.overall_result || "N/A"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {test.actual_completion_at
                                  ? formatDateTime(test.actual_completion_at)
                                  : "N/A"}
                              </TableCell>
                            </TableRow>
                          </CollapsibleTrigger>
                          <CollapsibleContent asChild>
                            <>
                              {test.attributes
                                .filter((a: any) => a.is_specification)
                                .map((attr: any) => (
                                  <SpecificationStatusRow
                                    key={attr.id}
                                    attribute={attr}
                                  />
                                ))}
                            </>
                          </CollapsibleContent>
                        </>
                      </Collapsible>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Additional Checks */}
            <div>
              <h5 className="font-medium mb-3">Additional Checks</h5>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={
                      data.batch.safe_packaging_practice_checked || false
                    }
                    onCheckedChange={(checked) =>
                      handleCheckboxChange(
                        "safe_packaging_practice_checked",
                        checked as boolean,
                      )
                    }
                    disabled={updateBatchMutation.isPending}
                  />
                  <div>
                    <Label>Safe Packaging Practice (PPE, etc.)</Label>
                    {data.batch.safe_packaging_practice_notes && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {data.batch.safe_packaging_practice_notes}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={data.batch.foreign_object_check_passed || false}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange(
                        "foreign_object_check_passed",
                        checked as boolean,
                      )
                    }
                    disabled={updateBatchMutation.isPending}
                  />
                  <div>
                    <Label>Free of Foreign Objects</Label>
                    {data.batch.foreign_object_check_notes && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {data.batch.foreign_object_check_notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Overall Summary */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium">Overall Clearance Status</h5>
                  <p className="text-sm text-muted-foreground">
                    {isAllClearanceComplete
                      ? "All clearance criteria met. Ready for final decision."
                      : "Some clearance criteria not met. Review required."}
                  </p>
                </div>
                <Badge
                  variant={isAllClearanceComplete ? "default" : "destructive"}
                  className="text-sm"
                >
                  {isAllClearanceComplete ? "✓ All Clear" : "⚠ Issues"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

interface SpecificationStatusRowProps {
  attribute: ClearanceTestAttribute;
}

function SpecificationStatusRow({ attribute }: SpecificationStatusRowProps) {
  // In the new model the test_template_attribute is joined in as
  // `testTemplateAttribute` and the recorded value fields live directly on
  // the test_instance_attribute row.
  const attributeTypeDetails = attribute.testTemplateAttribute;
  const specResult = attributeTypeDetails
    ? computeSpecStatus({ ...attribute, kind: attributeTypeDetails.kind })
    : ({ status: "not-applicable" } as SpecStatusResult);

  let targetDisplay = "N/A";
  let actualDisplay = "N/A";

  if (attribute.is_specification && attributeTypeDetails) {
    if (attributeTypeDetails.kind === AttributeKindEnum.SINGLE_NUMBER) {
      const target = attribute.spec_target_value_number
        ? Number(attribute.spec_target_value_number)
        : null;
      if (target !== null) {
        let range = formatNumber(target);
        if (specResult.difference) {
          const { lower, upper, symmetric } = specResult.difference;
          if (symmetric) {
            range = `${formatNumber(target)} ± ${formatNumber(upper)}`;
          } else {
            range = `${formatNumber(target)} (${formatNumber(
              target - lower,
            )} - ${formatNumber(target + upper)})`;
          }
        }
        const targetUnit =
          attribute.spec_target_value_unit ??
          attributeTypeDetails.measurement_default_unit ??
          "";
        targetDisplay = `${range} ${getDisplaySymbol(targetUnit)}`.trim();
      }

      actualDisplay =
        attribute.number_value !== null && attribute.number_value !== undefined
          ? `${formatNumber(Number(attribute.number_value))} ${getDisplaySymbol(
              attribute.measurement_unit ??
                attributeTypeDetails.measurement_default_unit ??
                "",
            )}`.trim()
          : "Not Recorded";
    } else if (
      attributeTypeDetails.kind === AttributeKindEnum.COMPUTED_NUMBER
    ) {
      const target = attribute.spec_target_value_number
        ? Number(attribute.spec_target_value_number)
        : null;
      if (target !== null) {
        let range = formatNumber(target);
        if (specResult.difference) {
          const { lower, upper, symmetric } = specResult.difference;
          if (symmetric) {
            range = `${formatNumber(target)} ± ${formatNumber(upper)}`;
          } else {
            range = `${formatNumber(target)} (${formatNumber(
              target - lower,
            )} - ${formatNumber(target + upper)})`;
          }
        }
        const targetUnit =
          attribute.spec_target_value_unit ??
          attributeTypeDetails.computed_number_result_display_unit ??
          "";
        targetDisplay = `${range} ${getDisplaySymbol(targetUnit)}`.trim();
      }

      const computedNumeric =
        attribute.computed_number !== null &&
        attribute.computed_number !== undefined
          ? Number(attribute.computed_number)
          : null;
      const actualUnit =
        attribute.computed_number_unit ??
        attributeTypeDetails.computed_number_result_display_unit ??
        "";
      actualDisplay =
        computedNumeric !== null && !Number.isNaN(computedNumeric)
          ? `${formatNumber(computedNumeric)} ${getDisplaySymbol(actualUnit)}`.trim()
          : "Not Recorded";
    } else if (attributeTypeDetails.kind === AttributeKindEnum.CHECK) {
      targetDisplay =
        attribute.spec_target_check_value === true
          ? "Checked"
          : attribute.spec_target_check_value === false
            ? "Unchecked"
            : "No Target";

      actualDisplay =
        attribute.check_value === true
          ? "Checked"
          : attribute.check_value === false
            ? "Unchecked"
            : "Not Recorded";
    }
  }

  return (
    <TableRow>
      <TableCell className="pl-12">
        {attributeTypeDetails?.name ?? ""}
      </TableCell>
      <TableCell>{targetDisplay}</TableCell>
      <TableCell
        className={cn("font-semibold", getSpecStatusColor(specResult.status))}
      >
        {actualDisplay}
      </TableCell>
      <TableCell>
        <Badge
          className={cn(getSpecStatusColor(specResult.status))}
          variant={specResult.status === "in-spec" ? "default" : "outline"}
        >
          {toTitleCase(specResult.status.replace(/-/g, " "))}
        </Badge>
      </TableCell>
    </TableRow>
  );
}
